// src/components/panel/PaymentMethodsChart.js
import React, { useState, useEffect, useCallback } from 'react';
import { CreditCardOutlined } from '@ant-design/icons';
import { Spin, Dropdown } from 'antd';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import analyticsV2Service, { getDateRanges } from '../../services/analyticsV2Service';

// Color palette for payment methods
const COLORS = {
    '201': '#1890ff', // Contado - Blue
    '202': '#52c41a', // Débito - Green
    '203': '#faad14', // Crédito - Orange
    '204': '#722ed1', // Cta Cte - Purple
    '205': '#eb2f96', // Cheque - Pink
    '206': '#13c2c2', // Transferencia - Cyan
    '207': '#fa8c16', // Otros electrónicos - Gold
    '208': '#8c8c8c', // Otra - Gray
};

function PaymentMethodsChart() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('week');
    const [monthOption, setMonthOption] = useState('currentMonth'); // 'currentMonth' or 'prevMonth'

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            
            // Get date ranges
            const ranges = getDateRanges();
            
            // Determine which date range to use based on period and month option
            let dateRange;
            if (period === 'day') {
                dateRange = ranges.today;
            } else if (period === 'week') {
                dateRange = ranges.week;
            } else if (period === 'month') {
                dateRange = monthOption === 'currentMonth' ? ranges.currentMonth : ranges.prevMonth;
            }
            
            const response = await analyticsV2Service.getPaymentMethods(dateRange.start, dateRange.end);
            
            if (response.success && response.data) {
                setData(response.data);
            } else {
                setData(null);
            }
            setError(null);
        } catch (err) {
            console.error('Error loading payment methods:', err);
            setError('Error al cargar métodos de pago');
        } finally {
            setLoading(false);
        }
    }, [period, monthOption]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handlePeriodChange = (newPeriod) => {
        setPeriod(newPeriod);
        // Reset monthOption when switching to month
        if (newPeriod === 'month') {
            setMonthOption('currentMonth');
        }
    };

    const handleMonthOptionChange = (option) => {
        setMonthOption(option);
    };

    // Month dropdown menu items
    const monthMenuItems = {
        items: [
            {
                key: 'currentMonth',
                label: 'Este Mes',
                onClick: () => handleMonthOptionChange('currentMonth')
            },
            {
                key: 'prevMonth',
                label: 'Mes Pasado',
                onClick: () => handleMonthOptionChange('prevMonth')
            }
        ]
    };

    if (loading) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <CreditCardOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>MÉTODOS DE PAGO</h2>
                </div>
                <div className='card-content-center'>
                    <Spin tip="Cargando métodos de pago..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <CreditCardOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>MÉTODOS DE PAGO</h2>
                </div>
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error}
                </div>
            </div>
        );
    }

    const chartData = data?.paymentMethods?.map(pm => ({
        name: pm.name,
        size: pm.totalAmount,
        percentage: pm.percentage,
        count: pm.count,
        code: pm.code,
        fill: COLORS[pm.code] || '#8c8c8c'
    })) || [];

    const totalSales = data?.totalSales || 0;

    // Custom content renderer for TreeMap
    const CustomizedContent = (props) => {
        const { x, y, width, height, name, size, percentage, count, fill, root, depth, index } = props;
        
        // Don't render if too small or if it's the root node
        if (width < 50 || height < 30 || depth === 0) return null;

        // Get the actual data from the payload
        const value = size || 0;
        const displayName = name || '';
        const displayPercentage = percentage || 0;

        return (
            <g>
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    style={{
                        fill,
                        stroke: '#fff',
                        strokeWidth: 2,
                    }}
                />
                {width > 80 && height > 50 && (
                    <>
                        <text
                            x={x + width / 2}
                            y={y + height / 2 - 10}
                            textAnchor="middle"
                            fill="#000000"
                            fontSize={14}
                            fontWeight="normal"
                        >
                            {displayName}
                        </text>
                        <text
                            x={x + width / 2}
                            y={y + height / 2 + 8}
                            textAnchor="middle"
                            fill="#000000"
                            fontSize={12}
                            fontWeight="normal"
                        >
                            ${value.toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                        </text>
                        <text
                            x={x + width / 2}
                            y={y + height / 2 + 24}
                            textAnchor="middle"
                            fill="#000000"
                            fontSize={11}
                            fontWeight="normal"
                        >
                            {displayPercentage.toFixed(1)}%
                        </text>
                    </>
                )}
            </g>
        );
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload || payload[0];
            const size = data.size || 0;
            const count = data.count || 0;
            const percentage = data.percentage || 0;
            const name = data.name || '';
            
            return (
                <div style={{
                    background: 'white',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{name}</p>
                    <p style={{ margin: '4px 0 0 0', color: '#1890ff' }}>
                        ${size.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </p>
                    <p style={{ margin: '2px 0 0 0', color: '#8c8c8c', fontSize: '12px' }}>
                        {count} facturas ({percentage.toFixed(1)}%)
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className='card'>
            <div className='card-inner'>
                <CreditCardOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <h2>MÉTODOS DE PAGO</h2>
            </div>

            {/* Period Toggle - using same style as SalesByCategory */}
            <div className='category-period-selector' style={{ marginTop: '8px', marginBottom: '12px' }}>
                <button 
                    className={`category-period-btn ${period === 'day' ? 'active' : ''}`}
                    onClick={() => handlePeriodChange('day')}
                >
                    Hoy
                </button>
                <button 
                    className={`category-period-btn ${period === 'week' ? 'active' : ''}`}
                    onClick={() => handlePeriodChange('week')}
                >
                    Semana
                </button>
                <Dropdown 
                    menu={monthMenuItems} 
                    trigger={['click']}
                    placement="bottom"
                >
                    <button 
                        className={`category-period-btn ${period === 'month' ? 'active' : ''}`}
                        onClick={(e) => {
                            if (period !== 'month') {
                                handlePeriodChange('month');
                            }
                        }}
                    >
                        {period === 'month' 
                            ? (monthOption === 'currentMonth' ? 'Este Mes' : 'Mes Pasado')
                            : 'Mes'
                        } ▾
                    </button>
                </Dropdown>
            </div>

            {/* Chart */}
            <div style={{ height: '170px', marginBottom: '8px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                        data={chartData}
                        dataKey="size"
                        aspectRatio={4 / 3}
                        stroke="#fff"
                        fill="#8884d8"
                        content={<CustomizedContent />}
                    >
                        <Tooltip content={<CustomTooltip />} />
                    </Treemap>
                </ResponsiveContainer>
            </div>

            {/* Total */}
            <div style={{ 
                textAlign: 'center', 
                paddingTop: '6px', 
                borderTop: '1px solid #f0f0f0' 
            }}>
                <div style={{ fontSize: '0.85rem', color: '#8c8c8c', marginBottom: '2px' }}>
                    Total Ventas
                </div>
                <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#1890ff' }}>
                    ${totalSales.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </div>
            </div>
        </div>
    );
}

export default PaymentMethodsChart;
